import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';

const RESOLUTION_LABELS = {
    0: { text: 'קיים', color: '#388e3c' },
    1: { text: 'לפי שם', color: '#1976d2' },
    2: { text: 'כרטיס חדש', color: '#f57c00' },
    3: { text: 'כרטיס חדש + מזהה חיצוני', color: '#d32f2f' },
};

const SumitClosingBalanceImportModal = ({
    open,
    onClose,
    previewSumitClosingBalance,
    commitSumitClosingBalance,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    user
}) => {
    const customStyles = {
        container: (provided) => ({ ...provided, width: '100%', boxSizing: 'border-box !important' }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': { border: '2px solid var(--Foundation-Blue-Normal, #304FFF)' },
        }),
        placeholder: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        singleValue: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        menu: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#F9AA2A' : state.isFocused ? '#ebebeb' : null,
            color: 'black',
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 2002 }),
    };

    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({ label: type, value: type }));
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    const [balanceFile, setBalanceFile] = useState(null);
    const [profitLossFile, setProfitLossFile] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [previewData, setPreviewData] = useState(null);
    const [missingPalCodes, setMissingPalCodes] = useState([]);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    // State for creating missing PalCodes
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({ palCodeId: '', palCodeType: '', palCodeCategory: null });
    const [bulkForm, setBulkForm] = useState({ palCodeType: '', palCodeCategory: null });

    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;
        const palCodesInCategory = palCodes.filter(pc => pc.palCodeCategory === category);
        const maxOrderNumber = palCodesInCategory.reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);
        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleBalanceDrop = files => {
        if (files?.length) setBalanceFile(files[0]);
    };

    const handleProfitLossDrop = files => {
        if (files?.length) setProfitLossFile(files[0]);
    };

    const handlePreview = async () => {
        if (!balanceFile) return;

        try {
            setProcessing(true);
            const res = await previewSumitClosingBalance(balanceFile, year, profitLossFile);
            setPreviewData(res);
            setMissingPalCodes(res?.missingPalCodes ?? []);
            setPreviewDone(true);
        } catch (err) {
            console.error('Sumit closing balance preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const calculatedOrderNumber = palCodeForm.palCodeCategory
                ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                : 100;

            const newPalCode = {
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: calculatedOrderNumber,
                accountantId: user.accountantId,
            };

            const response = await createMissingPalCode(newPalCode);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('PalCode creation failed:', errorText);
                throw new Error(errorText);
            }

            setMissingPalCodes(prev => prev.filter(pc => pc !== palCodeName));
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) return;

        try {
            setProcessing(true);
            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            const createPromises = selectedPalCodesForBulk.map((palCodeName, index) => {
                const newPalCode = {
                    palCodeId: palCodeName,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user.accountantId,
                };
                return createMissingPalCode(newPalCode);
            });

            const responses = await Promise.all(createPromises);
            const failed = responses.filter(r => !r.ok);
            if (failed.length > 0) {
                console.error('Some PalCodes failed to create');
                throw new Error(`${failed.length} PalCodes failed to create`);
            }

            setMissingPalCodes(prev => prev.filter(pc => !selectedPalCodesForBulk.includes(pc)));
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({ palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const startEditingPalCode = (palCodeName) => {
        setEditingPalCode(palCodeName);
        setPalCodeForm({ palCodeId: palCodeName, palCodeType: '', palCodeCategory: null });
    };

    const handleCommit = async () => {
        if (!balanceFile) return;

        try {
            setProcessing(true);
            const res = await commitSumitClosingBalance(balanceFile, year, profitLossFile);

            if (res?.errorMessages?.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Sumit closing balance import error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setBalanceFile(null);
        setProfitLossFile(null);
        setPreviewDone(false);
        setPreviewData(null);
        setMissingPalCodes([]);
        setEditingPalCode(null);
        setBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setBulkForm({ palCodeType: '', palCodeCategory: null });
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const getResolutionLabel = (status) => RESOLUTION_LABELS[status] || { text: '?', color: '#666' };

    if (!open) return null;

    const entries = previewData?.entries ?? [];

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            מאזן בוחן - Sumit
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>מאזן בוחן (Excel)</h5>
                            <DropzoneArea
                                key={balanceFile ? balanceFile.name : 'balance-empty'}
                                onDrop={handleBalanceDrop}
                                accept=".xlsx,.xls"
                                filesLimit={1}
                            />
                            {balanceFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {balanceFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>רווח והפסד (Excel) - אופציונלי</h5>
                            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 8px 0' }}>
                                התוצאה הסופית (רווח/הפסד) תירשם לכרטיס "עודפים"
                            </p>
                            <DropzoneArea
                                key={profitLossFile ? profitLossFile.name : 'pl-empty'}
                                onDrop={handleProfitLossDrop}
                                accept=".xlsx,.xls"
                                filesLimit={1}
                            />
                            {profitLossFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {profitLossFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>שנה</h5>
                            <TextField
                                type="number"
                                value={year}
                                onChange={e => setYear(parseInt(e.target.value) || new Date().getFullYear() - 1)}
                                size="small"
                                inputProps={{ min: 2000, max: 2100 }}
                                sx={{ width: 150 }}
                            />
                            <span style={{ marginRight: 10, fontSize: '13px', color: '#666' }}>
                                (תאריך מסמך: 31/12/{year})
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!balanceFile || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingPalCodes.length > 0}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && previewData && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {'שורות שנקראו'}: <strong>{previewData.parsedRows}</strong> |
                                    {' '}{'שורות עם יתרה'}: <strong>{entries.length}</strong> |
                                    {' '}{'כרטיסים חדשים'}: <strong>{previewData.newCardsToCreate}</strong>
                                </p>
                            </div>

                            {/* Missing PalCodes Section */}
                            {missingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: '14px', color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                if (!bulkMode) setBulkForm({ palCodeType: '', palCodeCategory: null });
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(opt) => setBulkForm({ ...bulkForm, palCodeType: opt?.value || '' })}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(opt) => setBulkForm({ ...bulkForm, palCodeCategory: opt?.value || null })}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: '12px', color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {missingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={processing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {missingPalCodes.map((palCodeName, idx) => (
                                        <div key={idx} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                            {editingPalCode === palCodeName ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                        size="small"
                                                        fullWidth
                                                        disabled
                                                    />
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                            onChange={(opt) => setPalCodeForm({ ...palCodeForm, palCodeType: opt?.value || '' })}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </label>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(opt) => setPalCodeForm({ ...palCodeForm, palCodeCategory: opt?.value || null })}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(palCodeName)}
                                                            disabled={processing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                        >
                                                            {t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {bulkMode && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPalCodesForBulk.includes(palCodeName)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedPalCodesForBulk(prev => [...prev, palCodeName]);
                                                                    } else {
                                                                        setSelectedPalCodesForBulk(prev => prev.filter(pc => pc !== palCodeName));
                                                                    }
                                                                }}
                                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            />
                                                        )}
                                                        <span style={{ fontWeight: 'bold' }}>{palCodeName}</span>
                                                    </div>
                                                    {!bulkMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => startEditingPalCode(palCodeName)}
                                                        >
                                                            {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {entries.length > 0 && (
                                <div style={{
                                    marginBottom: 20,
                                    padding: '15px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 4 }}>{'סיכום מאזן בוחן'}</h5>
                                    <p style={{ fontSize: '12px', color: '#888', margin: '0 0 15px 0' }}>
                                        {'סכומים שנקראו מקובץ מאזן הבוחן בלבד'}
                                    </p>
                                    <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'סה"כ חובה'}
                                            </div>
                                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#388e3c' }}>
                                                ₪{formatAmount(previewData.totalDebit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'סה"כ זכות'}
                                            </div>
                                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#d32f2f' }}>
                                                ₪{formatAmount(previewData.totalCredit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'הפרש'}
                                            </div>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: previewData.balance < 0 ? '#d32f2f' : previewData.balance > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(previewData.balance)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Profit & Loss Section */}
                            {previewData.profitLoss && (() => {
                                const pl = previewData.profitLoss;
                                const plIsCredit = pl.sortCode === '2';
                                const plResolution = getResolutionLabel(pl.cardResolutionStatus);
                                return (
                                    <div style={{
                                        marginBottom: 20,
                                        padding: '15px',
                                        backgroundColor: '#eef3ff',
                                        borderRadius: '8px',
                                        border: '1px solid #b9ccff'
                                    }}>
                                        <h5 style={{ marginTop: 0, marginBottom: 4 }}>{'רווח והפסד'}</h5>
                                        <p style={{ fontSize: '12px', color: '#5a6b8c', margin: '0 0 15px 0' }}>
                                            {`התוצאה הסופית (רווח/הפסד) שנקראה מקובץ הרווח והפסד, תירשם לכרטיס "${pl.cardName}"`}
                                        </p>
                                        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '150px' }}>
                                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                    {'תוצאה (רווח/הפסד)'}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: pl.amount < 0 ? '#d32f2f' : '#388e3c' }}>
                                                    ₪{formatAmount(pl.amount)}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1, minWidth: '150px' }}>
                                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                    {'חו/ז'}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: plIsCredit ? '#d32f2f' : '#388e3c' }}>
                                                    {plIsCredit ? 'זכות' : 'חובה'}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1, minWidth: '150px' }}>
                                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                    {'כרטיס יעד'}
                                                </div>
                                                <div style={{ fontSize: '15px', fontWeight: 'bold' }}>
                                                    {pl.cardName}{pl.resolvedCardId ? ` (${pl.resolvedCardId})` : ''}
                                                </div>
                                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: plResolution.color, marginTop: 2 }}>
                                                    {plResolution.text}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#5a6b8c', marginTop: 12 }}>
                                            {'תיאור: '}{pl.description}
                                        </div>
                                    </div>
                                );
                            })()}

                            {entries.length === 0 ? (
                                <p style={{ color: '#666' }}>{'לא נמצאו יתרות'}</p>
                            ) : (
                                <>
                                    <h5>{'יתרות סגירה'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'חשבון'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'שם'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'חשבון ראשי'}</th>
                                                <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd' }}>{'סכום'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'חו/ז'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'סטטוס כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'כרטיס'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.map((entry, i) => {
                                                const resolution = getResolutionLabel(entry.cardResolutionStatus);
                                                const isCredit = entry.sortCode === '2';
                                                const isMissingPalCode = entry.palCode && missingPalCodes.includes(entry.palCode);

                                                return (
                                                    <tr key={i} style={{
                                                        backgroundColor: isMissingPalCode
                                                            ? '#fff3cd'
                                                            : entry.cardResolutionStatus >= 2
                                                                ? '#fff8e1'
                                                                : i % 2 === 0 ? '#fff' : '#f9f9f9'
                                                    }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.custId}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.custName}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left', color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                            {entry.palCode || ''}
                                                            {isMissingPalCode && <span style={{ color: '#dc3545', marginLeft: 5 }}>*</span>}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            border: '1px solid #ddd',
                                                            textAlign: 'right',
                                                            color: isCredit ? '#d32f2f' : 'inherit',
                                                            fontWeight: isCredit ? 'bold' : 'normal'
                                                        }}>
                                                            {formatAmount(entry.amount)}
                                                        </td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {entry.sortCode === '1' ? 'חובה' : 'זכות'}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            border: '1px solid #ddd',
                                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                                            color: resolution.color,
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {resolution.text}
                                                        </td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {entry.resolvedCardId || '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SumitClosingBalanceImportModal;
